/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/products/product
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyProductKey,
  ShopifyProductOptionKey,
  ShopifyProductOptionToProductKey,
  ShopifyProductOptionToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyProductOptionKey,
  "2022-01": {
    name: "Shopify Product Option",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name" } },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "position" } },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyProductOptionToProductKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "product_id" } },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Values",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "values" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyProductOptionToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "product" },
  },
  "2024-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name", graphQLFieldKey: "name" } },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "position", graphQLFieldKey: "position" } },
      },
      {
        name: "Values",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "values", graphQLFieldKey: "values" } },
      },
    ],
    // synced as part of ShopifyProduct
    syncConfig: { type: "inherit" },
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["products/create", "products/update"],
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
            availableInWebhook: ["products/create", "products/update"],
          },
        },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "position",
            graphQLFieldKey: "position",
            availableInWebhook: ["products/create", "products/update"],
          },
        },
      },
      {
        name: "Values",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "values",
            graphQLFieldKey: "values",
            availableInWebhook: ["products/create", "products/update"],
          },
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyProductOptionToProductKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "product_id",
            availableInWebhook: ["products/create", "products/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
    ],
  },
};

export const ShopifyProductOption = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
